import { useState } from "react";
import axios from "axios";
import { Mail, Check, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const INTERESTS = [
  "Strandbar",
  "Beachvolleybaltornooi",
  "Kinderstrand",
  "Activiteiten",
];

export default function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [interests, setInterests] = useState([]);
  const [consent, setConsent] = useState(false);
  const [state, setState] = useState({ loading: false, ok: false, error: "" });

  const toggleInterest = (v) => {
    setInterests((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      setState({ loading: false, ok: false, error: "Toestemming is verplicht." });
      return;
    }
    setState({ loading: true, ok: false, error: "" });
    try {
      await axios.post(`${API}/api/newsletter`, {
        email: email.trim(),
        interests,
        consent,
      });
      setState({ loading: false, ok: true, error: "" });
      setEmail("");
      setInterests([]);
      setConsent(false);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Er ging iets mis. Probeer het straks opnieuw.";
      setState({ loading: false, ok: false, error: msg });
    }
  };

  return (
    <section
      data-testid="home-newsletter"
      className="relative bg-[#fdf8ec] border-b border-[#ebd9b5]/60"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="text-center">
          <span className="eyebrow">Nieuwsbrief</span>
          <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#2b1c10] leading-[1.1]">
            Blijf op de hoogte
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#6b5945] leading-relaxed max-w-2xl mx-auto">
            Wil je updates ontvangen over volgende edities, activiteiten en
            inschrijvingen? Laat je e-mailadres achter en duid aan waar je
            interesse in hebt.
          </p>
        </div>

        {state.ok ? (
          <div
            data-testid="newsletter-success"
            className="mt-10 rounded-2xl border border-[#ed6a1a]/30 bg-white p-6 sm:p-8 text-center shadow-[0_14px_36px_-18px_rgba(43,28,16,0.25)]"
          >
            <div className="mx-auto h-12 w-12 rounded-full bg-orange-gradient text-white flex items-center justify-center">
              <Check className="h-6 w-6" />
            </div>
            <p className="mt-4 font-display text-xl sm:text-2xl font-semibold text-[#2b1c10]">
              Bedankt! Je blijft op de hoogte van nieuws rond Mechelen Beach.
            </p>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            data-testid="newsletter-form"
            className="mt-10 rounded-2xl border border-[#ebd9b5] bg-white p-6 sm:p-8 shadow-[0_14px_36px_-18px_rgba(43,28,16,0.25)] space-y-6"
            noValidate
          >
            <div>
              <label
                htmlFor="newsletter-email"
                className="block text-sm font-semibold text-[#2b1c10] mb-2"
              >
                E-mailadres
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8a7460]" />
                <input
                  id="newsletter-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jij@voorbeeld.be"
                  data-testid="newsletter-email"
                  className="w-full pl-10 pr-3 py-3 rounded-xl border border-[#ebd9b5] bg-[#fdf8ec] text-[#2b1c10] placeholder:text-[#a3907a] focus:outline-none focus:border-[#ed6a1a] focus:ring-2 focus:ring-[#ed6a1a]/20 transition"
                />
              </div>
            </div>

            <div>
              <span className="block text-sm font-semibold text-[#2b1c10] mb-3">
                Interessegebieden
              </span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {INTERESTS.map((opt) => {
                  const checked = interests.includes(opt);
                  return (
                    <label
                      key={opt}
                      data-testid={`newsletter-interest-${opt.toLowerCase()}`}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${
                        checked
                          ? "border-[#ed6a1a] bg-[#fff3e3]"
                          : "border-[#ebd9b5] bg-[#fdf8ec] hover:border-[#f3c79a]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleInterest(opt)}
                        className="h-5 w-5 rounded border-[#c9b998] accent-[#ed6a1a] cursor-pointer"
                      />
                      <span className="text-sm sm:text-base text-[#2b1c10]">
                        {opt}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <label
              data-testid="newsletter-consent-label"
              className="flex items-start gap-3 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                required
                data-testid="newsletter-consent"
                className="mt-1 h-5 w-5 rounded border-[#c9b998] accent-[#ed6a1a] cursor-pointer flex-shrink-0"
              />
              <span className="text-sm text-[#4a3b2c] leading-relaxed">
                Ik geef toestemming om updates en nieuws over Mechelen Beach te
                ontvangen.
              </span>
            </label>

            {state.error && (
              <p
                data-testid="newsletter-error"
                className="text-sm text-[#c54508] bg-[#fde3d5] border border-[#f5b494] rounded-lg px-3 py-2"
              >
                {state.error}
              </p>
            )}

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <button
                type="submit"
                disabled={state.loading || !consent || !email}
                data-testid="newsletter-submit"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-orange-gradient text-white px-7 py-3.5 text-sm sm:text-base font-semibold shadow-[0_10px_28px_-8px_rgba(237,106,26,0.55)] hover:brightness-110 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {state.loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Bezig…
                  </>
                ) : (
                  "Hou mij op de hoogte"
                )}
              </button>
              <p className="text-xs text-[#6b5945] leading-relaxed sm:flex-1">
                We gebruiken je gegevens enkel om je updates over Mechelen Beach
                te sturen. Je kan je later altijd uitschrijven.
              </p>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
